// Timelapse do dia (B21, 7.2): o player sobre o campus que repete os quadros gravados no dia.
// Os quadros vêm de GET /api/timeline (src/http/timeline.ts). A conta (picos, barra↔hora,
// velocidade) está em timelapse-logica.ts. O campus mostra cada quadro pelo playback() e, durante
// o replay, fica em sépia (classe replay no canvas, style.css).

import type { TimelineFrame } from '../core/timeline.js';
import type { CampusRenderer } from './campus.js';
import { localeTag, t } from './i18n.js';
import {
  areaDaSerie,
  avancaRelogio,
  duracaoEmMinutos,
  horaCurta,
  indiceEm,
  instanteDaPosicao,
  picosDoDia,
  type Pico,
  type PontoSerie,
  posicaoDoInstante,
  serieDoDia,
  TIMELAPSE_VELOCIDADES,
  VELOCIDADE_INICIAL,
  velocidadeValida,
} from './timelapse-logica.js';

/** Altura do gráfico na viewBox. A largura é o número de minutos do dia. */
const GRAFICO_ALTURA = 40;

/** O player aberto: os quadros do dia, a série do gráfico e o relógio que anda sobre eles. */
interface Timelapse {
  data: string;
  quadros: TimelineFrame[] | 'carregando' | 'erro';
  serie: PontoSerie[];
  picos: Pico[];
  /** Quadro mostrado agora (índice em quadros). */
  indice: number;
  velocidade: number;
  tocando: boolean;
  /** Relógio simulado do dia (epoch ms): anda `velocidade` vezes mais rápido que o real. */
  relogio: number;
  ultimo: number;
  raf: number;
  el: HTMLElement;
  /** Selo "REPLAY 14:32" sobre o campus. */
  selo: HTMLElement;
}

let timelapse: Timelapse | undefined;
let campus: () => CampusRenderer | undefined = () => undefined;

/** Liga o player ao campus (main.ts): é o campus que mostra cada quadro no lugar do ao vivo. */
export function montaTimelapse(pegaCampus: () => CampusRenderer | undefined): void {
  campus = pegaCampus;
}

export function timelapseAberto(): boolean {
  return timelapse !== undefined;
}

/** Abre o player sobre o campus, no dia pedido, e busca os quadros desse dia. */
export function timelapseAbre(data: string): void {
  timelapseFecha();
  const el = document.createElement('section');
  el.className = 'timelapse-player';
  el.setAttribute('aria-label', t('tl.titulo'));
  el.innerHTML = `<header class="timelapse-cab"><span class="timelapse-titulo"></span><button class="timelapse-voltar" data-tl="fechar">${t('tl.voltar')}</button><button class="timelapse-fechar" data-tl="fechar" aria-label="${t('tl.fechar')}" title="${t('tl.fechar')}">✕</button></header>
    <p class="timelapse-aviso" aria-live="polite"></p>
    <div class="timelapse-controles">
      <button class="timelapse-tocar" data-tl="tocar"></button>
      <span class="timelapse-hora">--:--</span>
    </div>
    <div class="timelapse-trilha">
      <svg class="timelapse-grafico" viewBox="0 0 1 ${GRAFICO_ALTURA}" preserveAspectRatio="none" aria-hidden="true"><path class="presentes"></path><path class="trabalhando"></path></svg>
      <input class="timelapse-barra" type="range" min="0" max="0" value="0" step="1" aria-label="${t('tl.barra')}">
      <div class="timelapse-marcas" role="group" aria-label="${t('tl.picos')}"></div>
    </div>
    <div class="timelapse-linha">
      <div class="timelapse-velocidades" role="group" aria-label="${t('tl.velocidade')}">
        ${TIMELAPSE_VELOCIDADES.map((v) => `<button data-tl="vel" data-vel="${v}" aria-pressed="false">${v}×</button>`).join('')}
      </div>
      <p class="timelapse-quadro"></p>
    </div>
    <p class="timelapse-legenda"><span class="leg-presentes">${t('tl.legenda_presentes')}</span><span class="leg-trabalhando">${t('tl.legenda_trabalho')}</span></p>`;
  el.querySelector('.timelapse-titulo')!.textContent = `${t('tl.titulo')} · ${rotuloDia(data)}`;

  const selo = document.createElement('div');
  selo.className = 'timelapse-selo';
  selo.hidden = true;
  document.querySelector<HTMLElement>('#stage')!.append(el, selo);

  timelapse = { data, quadros: 'carregando', serie: [], picos: [], indice: 0, velocidade: VELOCIDADE_INICIAL, tocando: false, relogio: 0, ultimo: performance.now(), raf: 0, el, selo };
  el.addEventListener('click', timelapseClique);
  el.querySelector('.timelapse-barra')?.addEventListener('input', timelapseArrasta);
  campus()?.playback([]); // enquanto carrega, o campus fica vazio (não mostra o ao vivo por engano)
  timelapsePinta();
  el.querySelector<HTMLButtonElement>('.timelapse-fechar')?.focus(); // o tocar começa desabilitado, enquanto carrega
  timelapse.raf = requestAnimationFrame(timelapseFrame);

  fetch(`/api/timeline?date=${encodeURIComponent(data)}`)
    .then((r) => r.json() as Promise<({ ok: true; frames: TimelineFrame[] }) | { ok: false }>)
    .then((json) => {
      const tl = timelapse;
      if (!tl || tl.el !== el) return; // fechou ou abriu outro enquanto carregava
      if (json.ok) timelapseRecebe(json.frames);
      else {
        tl.quadros = 'erro';
        timelapsePinta();
      }
    })
    .catch(() => {
      const tl = timelapse;
      if (!tl || tl.el !== el) return;
      tl.quadros = 'erro';
      timelapsePinta();
    });
}

export function timelapseFecha(): void {
  const tl = timelapse;
  if (!tl) return;
  cancelAnimationFrame(tl.raf);
  tl.el.remove();
  tl.selo.remove();
  timelapse = undefined;
  campus()?.playback(null); // volta ao vivo
}

/** Chega o dia: monta a série, os picos e o gráfico, e começa no primeiro quadro. */
function timelapseRecebe(frames: TimelineFrame[]): void {
  const tl = timelapse;
  if (!tl) return;
  tl.quadros = frames;
  tl.serie = serieDoDia(frames);
  tl.picos = picosDoDia(tl.serie);
  tl.indice = 0;
  tl.relogio = frames[0]?.at ?? 0;
  timelapseDesenhaTrilha(tl);
  timelapseMostra();
  timelapsePinta();
}

/** Gráfico de presença e trabalho, barra e marcas dos picos. Feito uma vez por dia carregado. */
function timelapseDesenhaTrilha(tl: Timelapse): void {
  const quadros = Array.isArray(tl.quadros) ? tl.quadros : [];
  const inicio = quadros[0]?.at ?? 0;
  const fim = quadros[quadros.length - 1]?.at ?? 0;
  const total = Math.max(1, duracaoEmMinutos(inicio, fim));
  // presentes nunca é menor que trabalhando: o mesmo teto serve às duas áreas
  const teto = Math.max(1, ...tl.serie.map((p) => p.presentes));

  const svg = tl.el.querySelector<SVGSVGElement>('.timelapse-grafico')!;
  svg.setAttribute('viewBox', `0 0 ${total} ${GRAFICO_ALTURA}`);
  svg.querySelector<SVGPathElement>('.presentes')!.setAttribute('d', areaDaSerie(tl.serie, (p) => p.presentes, GRAFICO_ALTURA, teto));
  svg.querySelector<SVGPathElement>('.trabalhando')!.setAttribute('d', areaDaSerie(tl.serie, (p) => p.trabalhando, GRAFICO_ALTURA, teto));

  tl.el.querySelector<HTMLInputElement>('.timelapse-barra')!.max = String(total);
  tl.el.querySelector<HTMLElement>('.timelapse-marcas')!.innerHTML = tl.picos
    .map((pico, i) => {
      const rotulo = t('tl.pico', { h: horaCurta(pico.at), n: pico.trabalhando });
      return `<button type="button" class="timelapse-marca" data-tl="pico" data-pico="${i}" style="left:${(pico.minuto / total) * 100}%" aria-label="${rotulo}" title="${rotulo}"></button>`;
    })
    .join('');
}

function timelapseClique(event: Event): void {
  const tl = timelapse;
  const alvo = (event.target as HTMLElement).closest<HTMLElement>('[data-tl]');
  if (!tl || !alvo) return;
  if (alvo.dataset.tl === 'fechar') timelapseFecha();
  else if (alvo.dataset.tl === 'tocar') timelapseTocar();
  else if (alvo.dataset.tl === 'vel') {
    tl.velocidade = velocidadeValida(Number(alvo.dataset.vel));
    timelapsePinta();
  } else if (alvo.dataset.tl === 'pico') timelapsePulaPara(tl.picos[Number(alvo.dataset.pico)]);
}

/** Clique numa marca de pico: o quadro daquele minuto, sem mudar se o relógio está tocando. */
function timelapsePulaPara(pico: Pico | undefined): void {
  const tl = timelapse;
  if (!tl || !pico) return;
  tl.indice = pico.indice;
  tl.relogio = pico.at;
  timelapseMostra();
  timelapsePinta();
}

/** Arrastar a barra leva ao quadro daquele minuto do dia (o play continua do ponto). */
function timelapseArrasta(event: Event): void {
  const tl = timelapse;
  if (!tl || !Array.isArray(tl.quadros) || !tl.quadros.length) return;
  const inicio = tl.quadros[0]!.at;
  const fim = tl.quadros[tl.quadros.length - 1]!.at;
  tl.relogio = instanteDaPosicao(Number((event.target as HTMLInputElement).value), inicio, fim);
  tl.indice = indiceEm(tl.quadros, tl.relogio);
  timelapseMostra();
  timelapsePinta();
}

/** Play e pausa. No fim do dia, tocar de novo recomeça do primeiro quadro. */
function timelapseTocar(): void {
  const tl = timelapse;
  if (!tl || !Array.isArray(tl.quadros) || !tl.quadros.length) return;
  if (!tl.tocando && tl.indice === tl.quadros.length - 1) tl.indice = 0;
  tl.tocando = !tl.tocando;
  tl.relogio = tl.quadros[tl.indice]!.at;
  tl.ultimo = performance.now();
  timelapseMostra();
  timelapsePinta();
}

/** Quadro a quadro: o relógio simulado anda com a velocidade e troca o quadro quando passa do próximo. */
function timelapseFrame(now: number): void {
  const tl = timelapse;
  if (!tl) return;
  if (tl.tocando && Array.isArray(tl.quadros) && tl.quadros.length) {
    const fim = tl.quadros[tl.quadros.length - 1]!.at;
    tl.relogio = avancaRelogio(tl.relogio, now - tl.ultimo, tl.velocidade, fim);
    if (tl.relogio >= fim) tl.tocando = false;
    const indice = indiceEm(tl.quadros, tl.relogio);
    if (indice !== tl.indice) {
      tl.indice = indice;
      timelapseMostra();
    }
    timelapsePinta();
  }
  tl.ultimo = now;
  tl.raf = requestAnimationFrame(timelapseFrame);
}

/** Manda ao campus o quadro atual, com o selo da hora (sem quadro, ninguém aparece). */
function timelapseMostra(): void {
  const tl = timelapse;
  if (!tl) return;
  const quadro = Array.isArray(tl.quadros) ? tl.quadros[tl.indice] : undefined;
  campus()?.playback(quadro ? quadro.agentes : []);
  tl.selo.hidden = !quadro;
  tl.selo.textContent = quadro ? `${t('tl.replay')} ${horaCurta(quadro.at)}` : '';
}

/** Atualiza os controles do player (sem refazer o HTML: o foco do teclado fica onde estava). */
function timelapsePinta(): void {
  const tl = timelapse;
  if (!tl) return;
  const { el, quadros } = tl;
  const pronto = Array.isArray(quadros) && quadros.length > 0;
  const aviso = el.querySelector<HTMLElement>('.timelapse-aviso')!;
  aviso.textContent = quadros === 'carregando' ? t('tl.carregando') : quadros === 'erro' ? t('tl.erro') : pronto ? '' : t('tl.vazio');
  aviso.hidden = !aviso.textContent;

  const tocar = el.querySelector<HTMLButtonElement>('.timelapse-tocar')!;
  const rotulo = tl.tocando ? t('tl.pausar') : t('tl.tocar');
  tocar.textContent = tl.tocando ? '❚❚' : '▶';
  tocar.setAttribute('aria-label', rotulo);
  tocar.title = rotulo;
  tocar.disabled = !pronto;

  const barra = el.querySelector<HTMLInputElement>('.timelapse-barra')!;
  const horaEl = el.querySelector<HTMLElement>('.timelapse-hora')!;
  const resumo = el.querySelector<HTMLElement>('.timelapse-quadro')!;
  barra.disabled = !pronto;
  if (Array.isArray(quadros) && pronto) {
    const quadro = quadros[tl.indice]!;
    const ponto = tl.serie[tl.indice];
    barra.value = String(posicaoDoInstante(quadro.at, quadros[0]!.at, quadros[quadros.length - 1]!.at));
    horaEl.textContent = horaCurta(quadro.at);
    resumo.textContent = t('tl.resumo', { p: ponto?.presentes ?? 0, n: ponto?.trabalhando ?? 0 });
  } else {
    horaEl.textContent = '--:--';
    resumo.textContent = '';
  }

  for (const botao of el.querySelectorAll<HTMLButtonElement>('[data-vel]')) {
    botao.setAttribute('aria-pressed', String(Number(botao.dataset.vel) === tl.velocidade));
  }
}

/** Data de um dia (AAAA-MM-DD) por extenso, no idioma da tela. */
function rotuloDia(data: string): string {
  return new Date(`${data}T12:00:00`).toLocaleDateString(localeTag(), { weekday: 'long', day: 'numeric', month: 'long' });
}
